import { motion } from 'framer-motion';
import { MapPin, Mail, Clock, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const T = {
  heroTitle: { de: 'Kontakt', en: 'Contact' },
  heroSub: { de: 'Wir freuen uns auf Ihre Nachricht. Nehmen Sie Kontakt mit uns auf.', en: 'We look forward to your message. Get in touch with us.' },
  reachUs: { de: 'So erreichen Sie uns', en: 'How to Reach Us' },
  address: { de: 'Adresse', en: 'Address' },
  email: { de: 'E-Mail', en: 'Email' },
  phone: { de: 'Telefon', en: 'Phone' },
  hours: { de: 'Geschäftszeiten', en: 'Business Hours' },
  hoursVal: { de: 'Montag – Freitag: 09:00 – 17:00 Uhr\nSamstag – Sonntag: Geschlossen', en: 'Monday – Friday: 09:00 – 17:00\nSaturday – Sunday: Closed' },
  subsTitle: { de: 'Unsere Tochtergesellschaften', en: 'Our Subsidiaries' },
  subsSub: { de: 'Für spezifische Anfragen können Sie sich direkt an unsere Tochterunternehmen wenden.', en: 'For specific enquiries, you can contact our subsidiaries directly.' },
  logistics: { de: 'Logistik & Transport', en: 'Logistics & Transport' },
  consulting: { de: 'Unternehmensberatung', en: 'Business Consulting' },
  clearance: { de: 'Entrümpelung & Entsorgung', en: 'Clearance & Disposal' },
};

export default function Kontakt() {
  const { language } = useLanguage();
  const hoursLines = T.hoursVal[language].split('\n');

  return (
    <main data-testid="kontakt-page" className="pt-20">
      <section className="bg-[#0F1A2E] py-24 md:py-32">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="accent-line mb-6"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">{T.heroTitle[language]}</h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#0A1220]">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
              <div className="accent-line mb-6"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-8">{T.reachUs[language]}</h2>
              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="w-14 h-14 bg-[#0F1A2E] flex items-center justify-center flex-shrink-0"><MapPin size={24} className="text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">{T.address[language]}</h3>
                    <p className="text-white/70 leading-relaxed">Basic United Holding GmbH<br />Schneiderstraße 63<br />40764 Langenfeld (Rheinland)<br />{language === 'de' ? 'Deutschland' : 'Germany'}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-6">
                  <div className="w-14 h-14 bg-[#0F1A2E] flex items-center justify-center flex-shrink-0"><Mail size={24} className="text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">{T.email[language]}</h3>
                    <a href="mailto:info@basic-united.de" className="text-[#F5A623] hover:text-white transition-colors font-medium">info@basic-united.de</a>
                  </div>
                </div>
                <div className="flex items-start space-x-6">
                  <div className="w-14 h-14 bg-[#0F1A2E] flex items-center justify-center flex-shrink-0"><Phone size={24} className="text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">{T.phone[language]}</h3>
                    <a href="tel:+4921732046450" className="text-[#F5A623] hover:text-white transition-colors font-medium">02173 2046450</a>
                  </div>
                </div>
                <div className="flex items-start space-x-6">
                  <div className="w-14 h-14 bg-[#0F1A2E] flex items-center justify-center flex-shrink-0"><Clock size={24} className="text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">{T.hours[language]}</h3>
                    <p className="text-white/70">{hoursLines.map((line, i) => <span key={i}>{line}<br /></span>)}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              <div className="h-full min-h-[450px] border border-[#1e3a5f]">
                <iframe
                  title={language === 'de' ? 'Standort Basic United Holding GmbH' : 'Location Basic United Holding GmbH'}
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2513.7896835927385!2d6.9483!3d51.1089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8c8f3c8b8c8f3%3A0x47b8c8f3c8b8c8f3!2sSchneiderstra%C3%9Fe%2063%2C%2040764%20Langenfeld%20(Rheinland)%2C%20Germany!5e0!3m2!1sde!2sde!4v1703000000000!5m2!1sde!2sde"
                  width="100%" height="100%" style={{ border: 0, minHeight: '450px' }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="google-maps-embed"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#0F1A2E]">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">{T.subsTitle[language]}</h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">{T.subsSub[language]}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'AtoB Logistic Solutions GmbH', category: T.logistics[language], email: 'info@atob-logistics.de', website: 'www.atob-logistics.de', logo: '/images/atob-logistic-logo-transparent.png' },
              { name: 'DSB Business Solutions GmbH', category: T.consulting[language], email: 'info@dsb-deutschland.org', website: 'www.dsb-deutschland.org', logo: '/images/edited-dsb-logo.png' },
              { name: 'AtoB Objektservice GmbH', category: T.clearance[language], email: 'info@atob-objektservice.de', website: 'www.atob-objektservice.de', logo: '/images/atob-objektservice-logo-dark-bg.png' }
            ].map((company, index) => (
              <motion.div key={company.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 * (index + 1) }} className="bg-[#152238] p-8 rounded-xl border-l-4 border-[#F5A623]">
                <img src={company.logo} alt={company.name} className="h-12 w-auto object-contain mb-4" />
                <p className="text-sm text-white/50 mb-4">{company.category}</p>
                <div className="space-y-2">
                  <a href={`mailto:${company.email}`} className="block text-[#F5A623] hover:text-white transition-colors text-sm">{company.email}</a>
                  <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="block text-[#F5A623] hover:text-white transition-colors text-sm font-medium">{company.website}</a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
